import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { personalInfo } from '../data/mockData';
import { Mail, MapPin, Phone, Send, Twitter, Linkedin, Instagram, CheckCircle, Sparkles, Loader2 } from 'lucide-react';
import { EditableText, AdminEditToggle } from '../components/admin/InlineEditor';
import { TiltCard } from '../components/home/TiltCard';
import { ScrollDepthReveal } from '../components/home/ScrollDepthReveal';

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to send message');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Header />
      <main className="pt-20">
        {/* Hero */}
        <section className="relative py-16 lg:py-24 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/[0.05] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/[0.04] rounded-full blur-3xl" />
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-neutral-300 text-sm font-medium mb-6">
              <Sparkles size={14} className="mr-2" />Let's Connect
            </span>
            <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-white via-neutral-300 to-neutral-500 bg-clip-text text-transparent mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Get In Touch
            </h1>
            <p className="text-lg text-neutral-400 max-w-2xl mx-auto">Have a project in mind or want to collaborate? Let's connect!</p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <ScrollDepthReveal direction="left">
                <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Let's Start a Conversation</h2>
                <p className="text-neutral-400 mb-8 leading-relaxed">I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.</p>

                <div className="space-y-4">
                  {[{ icon: Mail, label: 'Email', value: 'hi@iambivas.in', href: 'mailto:hi@iambivas.in' },
                    { icon: MapPin, label: 'Location', value: 'India' },
                    { icon: Phone, label: 'Phone', value: 'Available upon request' }].map((item, index) => (
                    <TiltCard key={index} maxTilt={6} hoverScale={1.015} testId={`contact-info-tilt-${index}`} className="group">
                      <div className="absolute -inset-1 bg-gradient-to-r from-white/10 to-white/10 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="relative flex items-center p-4 bg-white/[0.04] backdrop-blur-sm rounded-xl border border-white/10 group-hover:border-white/30 transition-all shadow-md">
                        <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mr-4">
                          <item.icon className="text-white" size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{item.label}</h3>
                          {item.href ? <a href={item.href} className="text-neutral-400 hover:text-white transition-colors">{item.value}</a> : <p className="text-neutral-400">{item.value}</p>}
                        </div>
                      </div>
                    </TiltCard>
                  ))}
                </div>

                <div className="mt-8">
                  <h3 className="font-semibold text-white mb-4">Connect on Social</h3>
                  <div className="flex items-center space-x-3">
                    {[{ icon: Twitter, href: personalInfo.social.twitter }, { icon: Linkedin, href: personalInfo.social.linkedin }, { icon: Instagram, href: personalInfo.social.instagram }].map((social, index) => (
                      <a key={index} href={social.href} target="_blank" rel="noopener noreferrer" className="p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white hover:border-transparent hover:text-black text-neutral-300 transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-white/20">
                        <social.icon size={20} />
                      </a>
                    ))}
                  </div>
                </div>
              </ScrollDepthReveal>

              {/* Form */}
              <ScrollDepthReveal direction="right" delay={0.1} className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-white/15 via-white/10 to-white/15 rounded-2xl blur-lg opacity-60" />
                <div className="relative bg-white/[0.04] backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-xl shadow-black/30">
                  {isSubmitted ? (
                    <div className="h-full flex flex-col items-center justify-center text-center py-12">
                      <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 shadow-lg shadow-white/20">
                        <CheckCircle size={48} className="text-black" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                      <p className="text-neutral-400">Thank you for reaching out. I'll get back to you soon!</p>
                      <p className="text-sm text-neutral-300 mt-2">Your message will be delivered to bivasmishra1@zohomail.in</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {error && (
                        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                          {error}
                        </div>
                      )}
                      {['name', 'email', 'subject'].map((field) => (
                        <div key={field}>
                          <label htmlFor={field} className="block text-sm font-medium text-neutral-300 mb-2 capitalize">{field === 'email' ? 'Email Address' : `Your ${field}`}</label>
                          <input type={field === 'email' ? 'email' : 'text'} id={field} name={field} value={formData[field]} onChange={handleChange} required className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-neutral-500 focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/10 transition-all" placeholder={field === 'email' ? 'john@example.com' : field === 'name' ? 'John Doe' : 'How can I help you?'} />
                        </div>
                      ))}
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-neutral-300 mb-2">Message</label>
                        <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-neutral-500 focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/10 transition-all resize-none" placeholder="Tell me about your project..." />
                      </div>
                      <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-white text-black font-semibold rounded-xl hover:bg-neutral-200 transition-all duration-300 disabled:opacity-50 flex items-center justify-center hover:shadow-lg hover:shadow-white/20">
                        {isSubmitting ? <><Loader2 size={18} className="animate-spin mr-2" />Sending...</> : <><Send size={18} className="mr-2" />Send Message</>}
                      </button>
                      <p className="text-xs text-neutral-500 text-center">Your message will be sent to bivasmishra1@zohomail.in</p>
                    </form>
                  )}
                </div>
              </ScrollDepthReveal>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <AdminEditToggle />
    </div>
  );
};

export default ContactPage;
